import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'

import { useCheckIfExists } from '@/hooks/useCheckIfExists'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import ConfirmationStep from '@/pages/create-consultation/components/ConfirmationStep'

import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'
import { Patient } from '@/core/models/patient.model'
import { Staff } from '@/core/models/staff.model'
import { useGetNetworksQuery } from '@/core/services/network'
import { LIST_ENDPOINT_MAX_LIMIT } from '@/core/utils/list-response.util'
import { useUpdateMeetingLinkOrderMutation } from '@/core/services/order'
import { OrderStatus } from '@/core/models/order.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { createConsultation, selectConsultationCreateRequestTracker, selectNewlyCreatedConsultationId } from '@/redux/reducers/consultation.reducer'
import {
  selectNewlyCreatedPatientId,
  selectPatientById,
  selectPatientCreateRequestTracker,
  selectPatients,
  createPatient,
} from '@/redux/reducers/patient.reducer'
import {
  selectCalendsoEventTypeByStaffId,
  selectStaffById,
  selectStaffCreateRequestTracker,
  selectStaffs,
  selectNewlyCreatedStaffId,
  addStaff,
} from '@/redux/reducers/staff.reducer'
import { selectWaitingRoomById } from '@/redux/reducers/waiting-room.reducer'

import { sanitizeStaffCreation } from '@/utils/staff'
import { sanitizeSecu } from '@/utils/helpers'
import { isStaff } from '@/utils/type-guards'
import { CreateConsultationStepperContext } from '../CreateConsultationStepper'
import CreateConsultationStepLayout from '../components/CreateConsultationStepLayout'
import { useGetPatientQuery } from '@/core/services/patient'

export type ConfirmationStepContainerProps = {
  onComplete?: () => void
}

export function ConfirmationStepContainer(props: ConfirmationStepContainerProps) {
  const { onComplete } = props
  const { values } = useContext(CreateConsultationStepperContext)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const newlyCreatedConsultationId = useAppSelector(selectNewlyCreatedConsultationId)

  const [searchParams] = useSearchParams()
  const [updateMeetingLinkOrder] = useUpdateMeetingLinkOrderMutation()

  const { data: networksResponse } = useGetNetworksQuery({ limit: LIST_ENDPOINT_MAX_LIMIT })
  const networks = networksResponse?.items
  const { data: patient } = useGetPatientQuery(values.patient ?? skipToken)

  const isGroupMode = values.isGroupConsultation && values.patients && values.patients.length >= 2

  const { dispatch, requestTracker: consultationCreateRequestTracker } = useRequestHandler<ReturnType<typeof createConsultation>['payload']>({
    onSuccess: async () => {
      enqueueSnackbar(t('notification.consultation.create.success'), { variant: 'success' })

      // Update order status when consultation was created from an order (via calendar card)
      const orderId = searchParams.get('orderId')
      if (orderId && newlyCreatedConsultationId) {
        try {
          await updateMeetingLinkOrder({
            id: orderId,
            consultation: newlyCreatedConsultationId,
            status: OrderStatus.Consultation_Created,
          }).unwrap()
        } catch {
          enqueueSnackbar(t('page.appointments.notification.startError'), { variant: 'error' })
        }
      }

      if (values.waitingRoom) {
        if (newlyCreatedConsultationId) {
          const livePath = isGroupMode
            ? `/consultations/${newlyCreatedConsultationId}/group-live`
            : `/consultations/${newlyCreatedConsultationId}/live`
          navigate(`${livePath}?${searchParams.toString()}`)
        }
      } else {
        onComplete?.()
      }
    },
    onError: () => enqueueSnackbar(t('notification.consultation.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const authUser = useAppSelector(selectAuthUser)
  const doctor = useAppSelector((state) =>
    isStaff(authUser) && authUser?.status === 'MEDECIN' ? authUser : values.medecin ? selectStaffById(state, values.medecin) : undefined
  )
  // const patient = useAppSelector((state) => (values.patient ? selectPatientById(state, values.patient) : undefined))
  const pds = useAppSelector((state) =>
    isStaff(authUser) && authUser?.status === 'PDS' ? authUser : values.pds ? selectStaffById(state, values.pds) : undefined
  )
  const doctorCalendsoEventTypes = useAppSelector((state) => (values.medecin ? selectCalendsoEventTypeByStaffId(state, values.medecin) : undefined))
  const waitingRoom = useAppSelector((state) => (values.waitingRoom ? selectWaitingRoomById(state, values.waitingRoom) : undefined))

  const checkStaff = useCheckIfExists<Staff>({
    successMsg: t('notification.medecin.create.success'),
    errorMsg: t('notification.medecin.create.error'),
    itemTracker: selectStaffCreateRequestTracker,
    createAction: (staff: Staff) => addStaff(sanitizeStaffCreation(_.omit(staff, ['_id', 'id', 'contactStatus']))),
    latestIDSelector: useAppSelector(selectNewlyCreatedStaffId),
    itemList: useAppSelector(selectStaffs),
    callback: (staff: Staff) => {
      if (!staff) return
      values.medecin = staff._id
      if (authUser) {
        dispatch(
          createConsultation({
            consultation: _.merge(
              {
                medecin: values.medecin!,
                patient: values.patient!,
                pds: authUser._id,
                startDate: values.startDate!,
                expectedDuration: values.expectedDuration,
                type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
                status: ConsultationStatus.Scheduled,
                reason: values.reason,
                speciality: values.speciality,
              },
              {
                orderId: searchParams.get('orderId'),
              }
            ),
            eventType: _.find(doctorCalendsoEventTypes?.events, ['id', Number(values.eventType)]),
          })
        )
      }
    },
  })

  const checkPatient = useCheckIfExists<Patient>({
    successMsg: t('notification.patient.create.success'),
    errorMsg: t('notification.patient.create.error'),
    itemTracker: selectPatientCreateRequestTracker,
    createAction: (patient: Patient) =>
      createPatient({
        netsoinsId: patient.netsoinsId,
        pluginId: patient.netsoinsId,
        userInfo: patient.userInfo,
        secu: sanitizeSecu(patient.secu),
        agreement: `from plugin original id: ${patient._id}`,
      }),
    latestIDSelector: useAppSelector(selectNewlyCreatedPatientId),
    itemList: useAppSelector(selectPatients),
    callback: (patient: Patient) => {
      console.log('🚀 ~ ConfirmationStepContainer ~ patient:', patient)
      if (!patient) return
      values.patient = patient._id
      if (isStaff(authUser) && authUser?.status === 'PDS' && values.medecin) {
        checkStaff(values.medecin)
      } else if (isStaff(authUser) && authUser?.status === 'MEDECIN' && values.pds) {
        dispatch(
          createConsultation({
            consultation: _.merge(
              {
                medecin: authUser._id,
                patient: values.patient!,
                pds: values.pds,
                startDate: values.startDate!,
                expectedDuration: values.expectedDuration,
                type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
                status: ConsultationStatus.Scheduled,
                reason: values.reason,
                speciality: values.speciality,
              },
              {
                orderId: searchParams.get('orderId'),
              }
            ),
          })
        )
      } else if (isStaff(authUser) && authUser?.status === 'MEDECIN' && values.pds === undefined) {
        dispatch(
          createConsultation({
            consultation: _.merge(
              {
                medecin: authUser._id,
                patient: values.patient!,
                startDate: values.startDate!,
                expectedDuration: values.expectedDuration,
                type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
                status: ConsultationStatus.Scheduled,
                reason: values.reason,
                speciality: values.speciality,
              },
              {
                orderId: searchParams.get('orderId'),
              }
            ),
          })
        )
      } else if (isStaff(authUser) && authUser?.status === 'PDS' && values.waitingRoom) {
        dispatch(
          createConsultation({
            consultation: _.merge(
              {
                patient: values.patient!,
                pds: authUser?._id,
                startDate: dayjs().toISOString(),
                expectedDuration: values.expectedDuration,
                type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
                status: ConsultationStatus.Scheduled,
                waitingRoom: values.waitingRoom,
                reason: values.reason,
                speciality: values.speciality,
              },
              {
                orderId: searchParams.get('orderId'),
              }
            ),
          })
        )
      } else if (isStaff(authUser) && authUser.status === 'PDS' && values.network) {
        dispatch(
          createConsultation({
            consultation: _.merge(
              {
                network: values.network!,
                patient: values.patient!,
                pds: authUser?._id,
                startDate: dayjs().toISOString(),
                expectedDuration: values.expectedDuration,
                type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
                status: ConsultationStatus.Scheduled,
              },
              {
                orderId: searchParams.get('orderId'),
              }
            ),
          })
        )
      }
    },
  })

  const handleCreateConsultation = () => {
    if (isGroupMode) {
      // Group mode: send patients[] directly, backend handles creation of individual consultations
      const baseConsultation: any = {
        patients: values.patients,
        startDate: values.startDate || dayjs().toISOString(),
        expectedDuration: values.expectedDuration,
        type: !values.meetingOnly ? ConsultationType.Classic : ConsultationType.MeetingOnly,
        status: ConsultationStatus.Scheduled,
        reason: values.reason,
        speciality: values.speciality,
      }

      if (isStaff(authUser) && authUser.status === 'PDS') {
        baseConsultation.pds = authUser._id
        if (values.medecin) baseConsultation.medecin = values.medecin
        if (values.waitingRoom) baseConsultation.waitingRoom = values.waitingRoom
        if (values.network) baseConsultation.network = values.network
      } else if (isStaff(authUser) && authUser.status === 'MEDECIN') {
        baseConsultation.medecin = authUser._id
        if (values.pds) baseConsultation.pds = values.pds
      }

      dispatch(createConsultation({ consultation: baseConsultation }))
      return
    }

    checkPatient(values.patient!)
  }

  return (
    <CreateConsultationStepLayout>
      <ConfirmationStep
        doctor={doctor ? _.trim(`${doctor.userInfo?.name || ''} ${doctor.userInfo?.surname || ''}`) : undefined}
        meetingOnly={values.meetingOnly}
        network={networks?.find((network) => network._id === values.network)?.name}
        patient={_.trim(`${patient?.userInfo.name || ''} ${patient?.userInfo.surname || ''}`)}
        isGroupConsultation={!!isGroupMode}
        pds={pds ? _.trim(`${pds.userInfo?.name || ''} ${pds.userInfo?.surname || ''}`) : undefined}
        reason={values.reason}
        speciality={values.speciality || ''}
        startDate={values.startDate ? dayjs(values.startDate).format('DD/MM/YYYY HH[h]mm') : undefined}
        waitingRoom={waitingRoom?.name}
        onSubmit={handleCreateConsultation}
        submitting={consultationCreateRequestTracker.loading}
      />
    </CreateConsultationStepLayout>
  )
}

export default ConfirmationStepContainer
